import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Plus,
  DollarSign,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Calendar,
  Receipt,
  X,
  CheckCircle
} from 'lucide-react'
import { getExpenses, getExpenseCategories, getFinanceSummary, recordExpensePayment } from '../../services/api'
import { format } from 'date-fns'
import clsx from 'clsx'
import { useAuth } from '../../contexts/AuthContext'

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-UG', {
    style: 'currency',
    currency: 'UGX',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export default function ExpenseList({ ranchId }) {
  const queryClient = useQueryClient()
  const { canWrite } = useAuth()
  const [categoryFilter, setCategoryFilter] = useState('')
  const [missingReceiptOnly, setMissingReceiptOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [paymentModal, setPaymentModal] = useState(null) // expense object or null

  const { data: expenses, isLoading, error } = useQuery({
    queryKey: ['expenses', ranchId, categoryFilter, missingReceiptOnly, page],
    queryFn: () => getExpenses(ranchId, {
      category_id: categoryFilter || undefined,
      missing_receipt: missingReceiptOnly || undefined,
      page,
      page_size: 20,
    }),
  })

  const { data: categories } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: getExpenseCategories,
  })

  const { data: summary } = useQuery({
    queryKey: ['financeSummary', ranchId],
    queryFn: () => getFinanceSummary(ranchId, 'month'),
  })

  if (error) {
    return (
      <div className="card p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Failed to load expenses</h3>
        <p className="text-gray-500 mt-1">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-500 mt-1">Track and manage ranch expenses</p>
        </div>
        {canWrite && (
          <Link to="/expenses/new" className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Link>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">This Month</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(summary.total_expenses)}
                </div>
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Income</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(summary.total_income)}
                </div>
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-center gap-3">
              <div className={clsx(
                'p-3 rounded-lg',
                summary.net_profit >= 0 ? 'bg-green-100' : 'bg-red-100'
              )}>
                <DollarSign className={clsx(
                  'h-6 w-6',
                  summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'
                )} />
              </div>
              <div>
                <div className="text-sm text-gray-500">Net Profit</div>
                <div className={clsx(
                  'text-2xl font-bold',
                  summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'
                )}>
                  {formatCurrency(summary.net_profit)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <select
            className="input w-full sm:w-64"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories?.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={missingReceiptOnly}
              onChange={(e) => { setMissingReceiptOnly(e.target.checked); setPage(1) }}
            />
            Missing receipt only
          </label>
        </div>
      </div>

      {/* Expenses List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : expenses?.items?.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No expenses found</h3>
            <p className="text-gray-500 mt-1">Add your first expense to get started</p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses?.items?.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(expense.expense_date), 'MMM d, yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {expense.description}
                      </div>
                      {expense.payee_name && (
                        <div className="text-sm text-gray-500">
                          Paid to: {expense.payee_name}
                        </div>
                      )}
                      {(!expense.receipt_urls || expense.receipt_urls.length === 0) && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                          No receipt
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        {expense.category_name || expense.category_code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        expense.approval_status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : expense.approval_status === 'pending'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-red-100 text-red-800'
                      )}>
                        {expense.approval_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {expense.payment_status === 'paid' ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          paid
                        </span>
                      ) : expense.approval_status === 'approved' && canWrite ? (
                        <button
                          onClick={() => setPaymentModal(expense)}
                          className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer"
                        >
                          unpaid - record
                        </button>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                          {expense.payment_status || 'unpaid'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {expenses && expenses.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Page {expenses.page} of {expenses.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn-secondary"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="btn-secondary"
                    disabled={page >= expenses.pages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Record Payment Modal */}
      {paymentModal && (
        <RecordPaymentModal
          expense={paymentModal}
          onClose={() => setPaymentModal(null)}
          onSuccess={() => {
            setPaymentModal(null)
            queryClient.invalidateQueries(['expenses'])
          }}
        />
      )}
    </div>
  )
}

function RecordPaymentModal({ expense, onClose, onSuccess }) {
  const { userId } = useAuth()
  const [formData, setFormData] = useState({
    payment_reference: '',
    payment_method: 'mobile_money',
    counterparty_msisdn: expense.payee_msisdn || '',
    proof_photo: null,
    recorded_by: userId,
  })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: (data) => recordExpensePayment(expense.id, data),
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to record payment'),
  })

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => setFormData(prev => ({ ...prev, proof_photo: e.target.result }))
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    if (!formData.payment_reference.trim()) {
      setError('Payment reference is required')
      return
    }
    mutation.mutate(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Record Payment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            {expense.description}
          </p>
          <p className="text-lg font-bold text-gray-900 mt-1">
            {formatCurrency(expense.amount)}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MoMo Reference Number *
            </label>
            <input
              type="text"
              required
              value={formData.payment_reference}
              onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="e.g. MTN-TXN-20260210-001"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payee Phone Number
            </label>
            <input
              type="tel"
              value={formData.counterparty_msisdn}
              onChange={(e) => setFormData({ ...formData, counterparty_msisdn: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="256XXXXXXXXX"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="mobile_money">Mobile Money</option>
              <option value="cash">Cash</option>
              <option value="bank">Bank Transfer</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proof Photo (optional)
            </label>
            {formData.proof_photo ? (
              <div className="relative inline-block">
                <img src={formData.proof_photo} alt="Proof" className="h-24 w-auto rounded-lg border" />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, proof_photo: null })}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="proof-upload"
                />
                <label htmlFor="proof-upload" className="flex flex-col items-center cursor-pointer">
                  <Receipt className="h-6 w-6 text-gray-400 mb-1" />
                  <span className="text-sm text-gray-600">Upload MoMo screenshot</span>
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              {mutation.isPending ? 'Recording...' : 'Confirm Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
